import { API } from "revolt.js";
import styles from "./Attachment.module.scss";
import classNames from "classnames";
import { useTriggerEvents } from "preact-context-menu";
import { useState } from "preact/hooks";

import { gifPlayable } from "../../../../../external/revolt.js/packages/revolt.js/src/preview";
import { useClient } from "../../../../controllers/client/ClientController";
import { useMessageAreaWidth } from "../../../../pages/channels/messaging/MessageAreaContext";
import AttachmentActions from "./AttachmentActions";
import GifFile from "./GifFile";
// import { SizedGrid } from "./Grid";
import ImageFile from "./ImageFile";
import Spoiler from "./Spoiler";
import TextFile from "./TextFile";

interface Props {
    attachment: API.File;
    hasContent?: boolean;
    allAttachments?: (API.File | API.Embed)[];
}

export default function Attachment({
    attachment,
    hasContent,
    allAttachments,
}: Props) {
    const client = useClient();
    const { filename, metadata } = attachment;
    const [spoiler, setSpoiler] = useState(filename.startsWith("SPOILER_"));

    const MAX_ATTACHMENT_WIDTH = Math.min(useMessageAreaWidth() / 3, 320);
    const MAX_ATTACHMENT_HEIGHT = 320;

    function makeSize(w: number, h: number) {
        return {
            width: Math.min(w, MAX_ATTACHMENT_WIDTH),
            height: Math.min(h, MAX_ATTACHMENT_HEIGHT),
        };
    }

    function getSize(file: API.File) {
        const meta = file.metadata;
        switch (meta.type) {
            case "Audio":
                return makeSize(200, 200);
            case "File":
                return makeSize(200, 200);
            case "Image":
                return makeSize(meta.width, meta.height);
            case "Text":
                return makeSize(200, 200);
            case "Video":
                return makeSize(meta.width, meta.height);
        }
    }

    const url = client.generateFileURL(
        attachment,
        { width: MAX_ATTACHMENT_WIDTH * 1.5 },
        true,
    );

    const size = getSize(attachment);

    switch (metadata.type) {
        case "Image": {
            return (
                <div
                    data-attachment
                    // style={size}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}
                    className={classNames({
                        [styles.margin]: hasContent,
                        spoiler,
                    })}>
                    {attachment.content_type === "image/gif" &&
                    !gifPlayable(attachment) ? (
                        <GifFile
                            allAttachments={allAttachments}
                            attachment={attachment}
                            {...size}
                        />
                    ) : (
                        <ImageFile
                            allAttachments={allAttachments}
                            attachment={attachment}
                            {...size}
                        />
                    )}
                    {spoiler && <Spoiler set={setSpoiler} />}
                </div>
            );
        }

        case "Video": {
            return (
                <div
                    data-attachment
                    className={classNames(styles.container, {
                        [styles.margin]: hasContent,
                    })}
                    style={{ "--width": `${metadata.width}px` }}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <AttachmentActions attachment={attachment} />
                    <div
                        // width={metadata.width}
                        // height={metadata.height}

                        {...size}
                        className={classNames({ spoiler })}>
                        <video
                            src={url}
                            alt={filename}
                            poster={client.generatePreviewFileURL(attachment)}
                            controls
                            preload="none"
                            // loading="lazy"
                            {...size}
                            style={size}
                            onClick={(ev) => {
                                const vid =
                                    ev.currentTarget as HTMLVideoElement;
                                if (vid.paused) {
                                    ev.preventDefault();
                                    vid.play();
                                }
                            }}
                            onMouseDown={(ev) =>
                                ev.button === 1 && window.open(url, "_blank")
                            }
                        />
                        {spoiler && <Spoiler set={setSpoiler} />}
                    </div>
                </div>
            );
        }

        case "Audio": {
            return (
                <div
                    data-attachment
                    className={classNames(styles.attachment, styles.audio)}
                    data-has-content={hasContent}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <AttachmentActions attachment={attachment} />
                    <audio src={url} controls />
                </div>
            );
        }

        case "Text": {
            return (
                <div
                    data-attachment
                    className={classNames(styles.attachment, styles.text)}
                    data-has-content={hasContent}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <TextFile attachment={attachment} />
                    <AttachmentActions attachment={attachment} />
                </div>
            );
        }

        default: {
            return (
                <div
                    data-attachment
                    className={classNames(styles.attachment, styles.file)}
                    data-has-content={hasContent}
                    {...useTriggerEvents("Menu", {
                        attachment,
                    })}>
                    <AttachmentActions attachment={attachment} />
                </div>
            );
        }
    }
}
